import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  type BuildingDto,
  type BuildingSummary,
  type ClientDetailDto,
  type ClientDto,
  type RoomDto,
  type UsageFunction,
} from "@klantpunt/shared";
import { UsageFunctionsField } from "../dossier/UsageFunctions";
import { AssetThumb } from "../assets/assetIcons";
import { ConfirmDelete, Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useClientContext } from "../../lib/clientContext";
import { useBuildingCache } from "./useBuilding";
import { AddressField, fmtAddress } from "../map/AddressField";
import { BaseMap } from "../map/BaseMap";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

/** Voorstel voor een nieuwe gebouwcode met het voorvoegsel van de klant: MOS001, MOS002, … */
export function nextCodeFor(prefix: string, codes: string[]): string {
  const re = new RegExp(`^${prefix}(\\d+)$`);
  const max = Math.max(0, ...codes.map((c) => Number(re.exec(c)?.[1] ?? 0)));
  return `${prefix}${String(max + 1).padStart(Math.min(3, 8 - prefix.length), "0")}`;
}
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * "Definitief verwijderen": pas actief als de gebouwcode is ingetypt. Wist het gebouw met alles erin
 * (ook tekeningen en foto's); niet terug te halen.
 */
export function PermanentDelete({
  building,
  detail,
  onDeleted,
}: {
  building: { id: string; code: string; name: string };
  detail: string;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const ok = typed.trim().toUpperCase() === building.code.toUpperCase();
  async function remove() {
    setBusy(true);
    try {
      await api(`/buildings/${building.id}/permanent`, { method: "DELETE", body: { code: typed.trim() } });
      toast(`${building.name} definitief verwijderd`);
      onDeleted();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }
  return (
    <details className="confirm">
      <summary className="btn small danger">Definitief verwijderen</summary>
      <div className="note danger-note" role="alert">
        <strong>{building.name} definitief verwijderen?</strong>
        <br />
        {detail} Dit kan niet ongedaan worden gemaakt.
      </div>
      <label className="field">
        <span className="label">Typ {building.code} om te bevestigen</span>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          style={{ textTransform: "uppercase" }}
        />
      </label>
      <button type="button" className="btn danger" onClick={remove} disabled={!ok || busy}>
        {busy ? "Verwijderen…" : "Gebouw definitief verwijderen"}
      </button>
    </details>
  );
}

/** Nieuw gebouw of gebouw bewerken (en archiveren of definitief verwijderen). */
export function BuildingDialog({
  building,
  onClose,
  onSaved,
  onRemoved,
}: {
  building?: BuildingDto;
  onClose: () => void;
  onSaved: (b: BuildingDto) => void;
  /** Gebouw gearchiveerd of definitief verwijderd. */
  onRemoved?: () => void;
}) {
  const toast = useToast();
  const [archiving, setArchiving] = useState(false);
  async function archive() {
    if (!building) return;
    setArchiving(true);
    try {
      await api(`/buildings/${building.id}`, { method: "DELETE" });
      toast(`${building.name} gearchiveerd`);
      onRemoved?.();
    } catch (e) {
      toast(errMsg(e));
      setArchiving(false);
    }
  }
  const [d, setD] = useState({
    name: building?.name ?? "",
    code: building?.code ?? "",
    address: building?.address ?? "",
    street: building?.street ?? "",
    houseNumber: building?.houseNumber ?? "",
    postcode: building?.postcode ?? "",
    city: building?.city ?? "",
    bagId: building?.bagId ?? "",
    robawsClientId: building?.robawsClientId ?? "",
    robawsProjectId: building?.robawsProjectId ?? "",
  });
  // Klant en locatie kies je bij een nieuw gebouw; verplaatsen naar een andere klant volgt (docs/CLIENTS.md §5).
  // Met een gekozen klant in de klantwisselaar staat die klant al klaar.
  const ctxClient = useClientContext().client;
  const [clientId, setClientId] = useState(!building && ctxClient && !ctxClient.isUnassigned ? ctxClient.id : "");
  const [siteId, setSiteId] = useState("");
  const [usage, setUsage] = useState<UsageFunction[]>(building?.usageFunctions ?? []);
  const clientList = useQuery({
    queryKey: ["clients"],
    queryFn: () => api<ClientDto[]>("/clients"),
    enabled: !building,
  });
  const clientDetail = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => api<ClientDetailDto>(`/clients/${clientId}`),
    enabled: !building && Boolean(clientId),
  });
  const allBuildings = useQuery({
    queryKey: ["buildings"],
    queryFn: () => api<BuildingSummary[]>("/buildings"),
    enabled: !building,
  });
  function pickClient(id: string) {
    setClientId(id);
    setSiteId("");
    const prefix = clientList.data?.find((c) => c.id === id)?.codePrefix;
    if (prefix && (!d.code || /^[A-Z]{2,5}\d+$/.test(d.code)))
      setD({
        ...d,
        code: nextCodeFor(
          prefix,
          (allBuildings.data ?? []).map((b) => b.code),
        ),
      });
  }
  // Codevoorstel voor de vooraf gekozen klant, zodra klanten en gebouwen geladen zijn.
  const suggested = useRef(false);
  useEffect(() => {
    if (suggested.current || building || !clientId || !clientList.data || !allBuildings.data) return;
    suggested.current = true;
    if (!d.code) pickClient(clientId);
  });
  const [loc, setLoc] = useState<{ lat: number; lng: number } | null>(
    building?.lat != null && building.lng != null ? { lat: building.lat, lng: building.lng } : null,
  );
  /** Opnieuw inzoomen na het kiezen van een adres. */
  const [fit, setFit] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof d) => (e: { target: { value: string } }) => setD({ ...d, [k]: e.target.value });
  const codeLocked = building?.codeLocked ?? false;

  async function save(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...d,
        code: d.code.toUpperCase(),
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
        usageFunctions: usage,
        ...(building ? {} : { clientId: clientId || null, siteId: siteId || null }),
      };
      const b = building
        ? await api<BuildingDto>(`/buildings/${building.id}`, {
            method: "PATCH",
            body: codeLocked ? { ...body, code: undefined } : body,
          })
        : await api<BuildingDto>("/buildings", { method: "POST", body });
      onSaved(b);
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={building ? "Gebouw bewerken" : "Nieuw gebouw"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => save()}
            disabled={busy || !d.name.trim() || !d.code.trim()}
          >
            Opslaan
          </button>
        </>
      }
    >
      <form onSubmit={save}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {building ? (
          <p className="hint">
            Klant: <strong>{building.client.name}</strong>
            {building.site && <> · locatie {building.site.name}</>}
          </p>
        ) : (
          <div className="grid2">
            <label className="field">
              <span className="label">Klant</span>
              <select value={clientId} onChange={(e) => pickClient(e.target.value)}>
                <option value="">Nog in te delen</option>
                {clientList.data
                  ?.filter((c) => !c.isUnassigned)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="field">
              <span className="label">Locatie</span>
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                disabled={!clientId || !clientDetail.data?.sites.length}
              >
                <option value="">{clientId ? "Geen locatie" : "Kies eerst een klant"}</option>
                {clientDetail.data?.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div className="grid2">
          <label className="field">
            <span className="label">Naam *</span>
            <input value={d.name} onChange={set("name")} placeholder="Kantoor De Hoef" required />
          </label>
          <label className="field">
            <span className="label">Code * (voorvoegsel van ID's)</span>
            <input
              value={d.code}
              onChange={set("code")}
              placeholder="HOEF"
              maxLength={8}
              disabled={codeLocked}
              style={{ textTransform: "uppercase" }}
              required
            />
          </label>
        </div>
        <p className="hint">
          {codeLocked
            ? "De code ligt vast zodra er bevindingen zijn, zodat ID's herkenbaar blijven."
            : `Bevindingen krijgen ID's als ${(d.code || "CODE").toUpperCase()}-0001.`}
        </p>
        <AddressField
          value={d.address}
          // Vrij getypt adres: niet meer gekoppeld aan de BAG (de locatie blijft staan).
          onText={(address) => setD({ ...d, address, street: "", houseNumber: "", postcode: "", city: "", bagId: "" })}
          onPick={(a) => {
            setD({
              ...d,
              address: fmtAddress(a),
              street: a.street ?? "",
              houseNumber: a.houseNumber ?? "",
              postcode: a.postcode ?? "",
              city: a.city ?? "",
              bagId: a.id,
            });
            setLoc({ lat: a.lat, lng: a.lng });
            setFit((n) => n + 1);
          }}
        />
        <p className="label">Locatie op de kaart</p>
        <BaseMap
          className="pickmap"
          label="Locatie van het gebouw"
          pins={loc ? [{ id: "b", ...loc, label: (d.code || "•").toUpperCase(), title: "Locatie van het gebouw" }] : []}
          draggable
          onDrag={(lat, lng) => setLoc({ lat, lng })}
          onPick={loc ? undefined : (lat, lng) => setLoc({ lat, lng })}
          fitKey={`${fit}`}
        />
        <div className="row">
          <p className="hint grow">
            {loc
              ? `${d.bagId ? "Locatie uit de BAG. " : ""}Sleep de pin als de ingang ergens anders ligt.`
              : "Kies een adres uit de lijst, of tik op de kaart om de plek aan te wijzen."}
          </p>
          {loc && (
            <button type="button" className="btn small" onClick={() => setLoc(null)}>
              Locatie wissen
            </button>
          )}
        </div>
        <UsageFunctionsField value={usage} onChange={setUsage} />
        <p className="label">Koppeling Robaws</p>
        <div className="grid2">
          <label className="field">
            <span>Robaws klant-ID</span>
            <input value={d.robawsClientId} onChange={set("robawsClientId")} placeholder="bijv. 1234" />
          </label>
          <label className="field">
            <span>Robaws project-ID</span>
            <input value={d.robawsProjectId} onChange={set("robawsProjectId")} placeholder="optioneel" />
          </label>
        </div>
        <button type="submit" hidden />
      </form>
      {building && onRemoved && (
        <section className="dangerzone" aria-label="Gebouw verwijderen">
          <p className="label">Gebouw verwijderen</p>
          <div className="btnrow">
            <ConfirmDelete
              label="Archiveren"
              confirmLabel="Gebouw archiveren"
              question={`${building.name} archiveren?`}
              detail="Het gebouw verdwijnt uit de lijst; alle gegevens blijven bewaard. Terugzetten kan via “Gearchiveerd” op de startpagina."
              onConfirm={archive}
              busy={archiving}
            />
            <PermanentDelete
              building={building}
              detail={`Alles gaat weg: ${plural(building.floors.length, "verdieping", "verdiepingen")} met tekeningen, ${plural(building.issueCount, "bevinding", "bevindingen")} met foto's en logboek, taken en ruimtes.`}
              onDeleted={onRemoved}
            />
          </div>
        </section>
      )}
    </Modal>
  );
}

/** Verdieping toevoegen (zonder tekening) of bewerken/verwijderen. */
export function FloorDialog({
  building,
  floorId,
  onClose,
  onSaved,
}: {
  building: BuildingDto;
  floorId?: string;
  onClose: () => void;
  onSaved: (b: BuildingDto, floorId?: string) => void;
}) {
  const floor = building.floors.find((f) => f.id === floorId);
  const [name, setName] = useState(floor?.name ?? "");
  const [level, setLevel] = useState(floor?.level ?? "");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const rooms = building.rooms.filter((r) => r.floorId === floorId);
  const insts = building.assets.filter((x) => x.floorId === floorId);

  async function save() {
    setBusy(true);
    try {
      if (floor) {
        onSaved(await api<BuildingDto>(`/floors/${floor.id}`, { method: "PATCH", body: { name, level } }));
      } else {
        const id = crypto.randomUUID();
        onSaved(
          await api<BuildingDto>(`/buildings/${building.id}/floors`, { method: "POST", body: { id, name, level } }),
          id,
        );
      }
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }
  async function remove() {
    if (!floor) return;
    setBusy(true);
    try {
      onSaved(await api<BuildingDto>(`/floors/${floor.id}`, { method: "DELETE" }));
      toast(`Verdieping “${floor.name}” verwijderd`);
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={floor ? "Verdieping bewerken" : "Verdieping toevoegen"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={busy || !name.trim()}>
            Opslaan
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid2">
          <label className="field">
            <span className="label">Naam</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="bijv. 2e verdieping" />
          </label>
          <label className="field">
            <span className="label">Afkorting</span>
            <input value={level} onChange={(e) => setLevel(e.target.value)} placeholder="bijv. 2" maxLength={10} />
          </label>
        </div>
        <p className="hint">
          De afkorting bepaalt de volgorde: K2, K1, BG, E (entresol), 1, 2 … D (dak). Schuiven kan daarna met de
          pijltjes.
        </p>
        <button type="submit" hidden />
      </form>
      {floor && (
        <>
          <p className="hint">
            {floor.plan ? `Tekening: ${floor.plan.name ?? "geüpload"}` : "Nog geen tekening"} ·{" "}
            {plural(rooms.length, "ruimte", "ruimtes")}
          </p>
          <p className="label">Verwijderen</p>
          <ConfirmDelete
            label="Verdieping verwijderen"
            question={`“${floor.name}” verwijderen?`}
            busy={busy}
            onConfirm={remove}
            detail={
              <>
                {rooms.length > 0 &&
                  `De ${plural(rooms.length, "ruimte", "ruimtes")} op deze verdieping ${rooms.length === 1 ? "wordt" : "worden"} ook verwijderd. `}
                Bevindingen blijven bestaan; hun plek op deze verdieping vervalt en dat komt in het logboek.
                {insts.length > 0 && ` ${plural(insts.length, "asset verliest", "assets verliezen")} hun locatie.`}
              </>
            }
          />
        </>
      )}
    </Modal>
  );
}

/** Ruimte toevoegen, optioneel direct intekenen. */
export function AddRoomDialog({
  floorId,
  hasPlan,
  onClose,
  onSaved,
}: {
  floorId: string;
  hasPlan: boolean;
  onClose: () => void;
  onSaved: (r: RoomDto, draw: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  async function save(draw: boolean) {
    setBusy(true);
    try {
      const r = await api<RoomDto>(`/floors/${floorId}/rooms`, {
        method: "POST",
        body: { id: crypto.randomUUID(), name },
      });
      onSaved(r, draw);
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Ruimte toevoegen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          {hasPlan && (
            <button type="button" className="btn" onClick={() => save(true)} disabled={busy || !name.trim()}>
              Opslaan en intekenen
            </button>
          )}
          <button type="button" className="btn primary" onClick={() => save(false)} disabled={busy || !name.trim()}>
            Opslaan
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void save(false);
        }}
      >
        <label className="field">
          <span className="label">Naam</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="bijv. Kantoor 2.04" />
        </label>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Ruimte bewerken: naam, assets, intekenen, vorm wissen, hoekpunten verslepen, verwijderen. */
export function RoomDialog({
  building,
  room,
  canDraw,
  onClose,
  onDraw,
  onEditVertices,
  onAddInstallation,
  onEditInstallation,
}: {
  building: BuildingDto;
  room: RoomDto;
  canDraw: boolean;
  onClose: () => void;
  onDraw: () => void;
  onEditVertices: () => void;
  onAddInstallation: () => void;
  onEditInstallation: (id: string) => void;
}) {
  const cache = useBuildingCache(building.id);
  const [name, setName] = useState(room.name);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const floor = building.floors.find((f) => f.id === room.floorId);
  const insts = building.assets.filter((x) => x.roomId === room.id);

  async function patch(body: Partial<RoomDto>, msg?: string) {
    setBusy(true);
    try {
      cache.upsertRoom(await api<RoomDto>(`/rooms/${room.id}`, { method: "PATCH", body }));
      if (msg) toast(msg);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    try {
      await api(`/rooms/${room.id}`, { method: "DELETE" });
      cache.removeRoom(room.id);
      toast(`Ruimte “${room.name}” verwijderd`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Ruimte bewerken"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => patch({ name }, "Ruimte opgeslagen")}
            disabled={busy || !name.trim()}
          >
            Opslaan
          </button>
        </>
      }
    >
      <label className="field">
        <span className="label">Naam</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <p className="hint">
        {floor?.name} · {room.shape ? "ingetekend op de tekening" : "nog niet ingetekend"}
        {insts.length > 0 && ` · ${plural(insts.length, "asset", "assets")}`}
      </p>
      <p className="label">Assets in deze ruimte</p>
      {insts.length ? (
        <ul className="list compact">
          {insts.map((x) => (
            <li key={x.id}>
              <AssetThumb asset={x} size={32} />
              <span className="grow">
                {x.name} <span className="muted">{x.code}</span>
              </span>
              <button type="button" className="btn small" onClick={() => onEditInstallation(x.id)}>
                Bewerken
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="hint">Nog geen assets in deze ruimte.</p>
      )}
      <div className="btnrow">
        <button type="button" className="btn small" onClick={onAddInstallation}>
          + Asset in deze ruimte
        </button>
      </div>
      {canDraw && (
        <div className="btnrow">
          <button type="button" className="btn small" onClick={onDraw}>
            {room.shape ? "Opnieuw intekenen" : "Intekenen"}
          </button>
          {room.shape && (
            <>
              <button type="button" className="btn small" onClick={onEditVertices}>
                Hoekpunten verslepen
              </button>
              <button type="button" className="btn small" onClick={() => patch({ shape: null }, "Vorm gewist")}>
                Vorm wissen
              </button>
            </>
          )}
        </div>
      )}
      <p className="label">Verwijderen</p>
      <ConfirmDelete
        label="Ruimte verwijderen"
        question={`“${room.name}” verwijderen?`}
        busy={busy}
        onConfirm={remove}
        detail={
          <>
            Bevindingen in deze ruimte blijven bestaan, maar zonder deze ruimte. Dit komt in hun logboek.
            {insts.length > 0 &&
              ` ${plural(insts.length, "asset verliest", "assets verliezen")} de koppeling met deze ruimte.`}
          </>
        }
      />
    </Modal>
  );
}
